import React, { useState } from 'react';
import CalcShell from '../CalcShell';
import { CNetRow, CNetActions, CNetResult } from './_cnet';

// ---------------- IPv4 utilities ----------------
function ipToInt(ip) {
  const parts = ip.split('.').map(Number);
  if (parts.length !== 4 || parts.some(x => Number.isNaN(x) || x < 0 || x > 255)) return null;
  return ((parts[0] << 24) >>> 0) + (parts[1] << 16) + (parts[2] << 8) + parts[3];
}
function intToIp(n) {
  n = n >>> 0;
  return [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join('.');
}
function maskFromCidr(c) {
  return c === 0 ? 0 : (0xFFFFFFFF << (32 - c)) >>> 0;
}
function ipClass(n) {
  const a = (n >>> 24) & 255;
  if (a < 128) return 'A';
  if (a < 192) return 'B';
  if (a < 224) return 'C';
  if (a < 240) return 'D';
  return 'E';
}
function isPrivate(n) {
  const [a, b] = intToIp(n).split('.').map(Number);
  if (a === 10) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  if (a === 127) return true;
  return false;
}
const CIDR_OPTS = [];
for (let c = 32; c >= 0; c--) CIDR_OPTS.push({ c, mask: c === 0 ? '0.0.0.0' : intToIp(maskFromCidr(c)) });

function IPv4() {
  const [cls, setCls] = useState('Any');
  const [cidr, setCidr] = useState(30);
  const [ip, setIp] = useState('47.11.3.17');
  const [r, setR] = useState(null);

  const calc = () => {
    const n = ipToInt(ip.trim());
    if (n == null) { setR({ error: 'Invalid IP address' }); return; }
    const mask = maskFromCidr(cidr);
    const network = (n & mask) >>> 0;
    const broadcast = (network | (~mask >>> 0)) >>> 0;
    const totalHosts = cidr === 32 ? 1 : cidr === 31 ? 2 : 2 ** (32 - cidr);
    const usable = cidr >= 31 ? 0 : totalHosts - 2;
    const first = cidr >= 31 ? network : network + 1;
    const last = cidr >= 31 ? broadcast : broadcast - 1;
    const rows = [];
    if (cidr >= 24) {
      const [a, b, c] = intToIp(n).split('.').map(Number);
      const netBlock = 2 ** (32 - cidr);
      for (let s = 0; s < 256; s += netBlock) {
        const netI = ((a << 24) + (b << 16) + (c << 8) + s) >>> 0;
        rows.push({
          net: intToIp(netI),
          range: cidr >= 31 ? intToIp(netI) + ' - ' + intToIp(netI + netBlock - 1) : `${intToIp(netI + 1)} - ${intToIp(netI + netBlock - 2)}`,
          bc: intToIp(netI + netBlock - 1),
        });
      }
    }
    setR({
      ip, network: intToIp(network), first: intToIp(first), last: intToIp(last), broadcast: intToIp(broadcast),
      totalHosts, usable, mask: intToIp(mask), wild: intToIp((~mask) >>> 0),
      binMask: intToIp(mask).split('.').map(x => (+x).toString(2).padStart(8, '0')).join('.'),
      cls: ipClass(n), cidr, priv: isPrivate(n),
      binaryId: n.toString(2).padStart(32, '0'),
      integerId: n,
      hexId: '0x' + n.toString(16).padStart(8, '0'),
      arpa: intToIp(n).split('.').reverse().join('.') + '.in-addr.arpa',
      mapped: '::ffff:' + n.toString(16).padStart(8, '0').match(/.{4}/g).join('.'),
      to4: '2002:' + n.toString(16).padStart(8, '0').match(/.{4}/g).join('.') + '::/48',
      short: `${ip} /${cidr}`,
      rows,
    });
  };

  return (
    <>
      <h3 className="cnet-section-title">IPv4 Subnet Calculator</h3>
      <div className="cnet-panel">
        <div className="cnet-row-2col">
          <div className="cnet-label">Network Class</div>
          <div className="flex gap-4 items-center pt-2 text-sm">
            {['Any', 'A', 'B', 'C'].map(k => (
              <label key={k} className="flex items-center gap-1">
                <input type="radio" name="cls" checked={cls === k} onChange={() => setCls(k)} />{k}
              </label>
            ))}
          </div>
        </div>
        <div className="cnet-row-2col">
          <div className="cnet-label">Subnet</div>
          <select data-testid="subnet-cidr" className="cnet-select" value={cidr} onChange={e => setCidr(+e.target.value)}>
            {CIDR_OPTS.map(o => <option key={o.c} value={o.c}>{`${o.mask} /${o.c}`}</option>)}
          </select>
        </div>
        <div className="cnet-row-2col">
          <div className="cnet-label">IP Address</div>
          <input data-testid="subnet-ip" className="cnet-input" value={ip} onChange={e => setIp(e.target.value)} />
        </div>
        <CNetActions onCalc={calc} onClear={() => setR(null)} testId="subnet-v4" />
      </div>
      {r && !r.error && (
        <CNetResult>
          <table className="cnet-info-table" data-testid="subnet-v4-table">
            <tbody>
              <tr><td>IP Address:</td><td>{r.ip}</td></tr>
              <tr><td>Network Address:</td><td>{r.network}</td></tr>
              <tr><td>Usable Host IP Range:</td><td>{r.first} - {r.last}</td></tr>
              <tr><td>Broadcast Address:</td><td>{r.broadcast}</td></tr>
              <tr><td>Total Number of Hosts:</td><td>{r.totalHosts.toLocaleString()}</td></tr>
              <tr><td>Number of Usable Hosts:</td><td>{r.usable.toLocaleString()}</td></tr>
              <tr><td>Subnet Mask:</td><td>{r.mask}</td></tr>
              <tr><td>Wildcard Mask:</td><td>{r.wild}</td></tr>
              <tr><td>Binary Subnet Mask:</td><td>{r.binMask}</td></tr>
              <tr><td>IP Class:</td><td>{r.cls}</td></tr>
              <tr><td>CIDR Notation:</td><td>/{r.cidr}</td></tr>
              <tr><td>IP Type:</td><td>{r.priv ? 'Private' : 'Public'}</td></tr>
              <tr><td>Short:</td><td>{r.short}</td></tr>
              <tr><td>Binary ID:</td><td className="break-all">{r.binaryId}</td></tr>
              <tr><td>Integer ID:</td><td>{r.integerId.toLocaleString()}</td></tr>
              <tr><td>Hex ID:</td><td>{r.hexId}</td></tr>
              <tr><td>in-addr.arpa:</td><td>{r.arpa}</td></tr>
              <tr><td>IPv4 Mapped Address:</td><td>{r.mapped}</td></tr>
              <tr><td>6to4 Prefix:</td><td>{r.to4}</td></tr>
            </tbody>
          </table>
          {r.rows.length > 0 && (
            <>
              <div className="mt-4 mb-2 font-bold text-slate-900">All {r.rows.length} of the Possible /{r.cidr} Networks for {r.ip.split('.').slice(0, 3).join('.') + '.*'}</div>
              <table className="data-table">
                <thead><tr><th>Network Address</th><th>Usable Host Range</th><th>Broadcast Address</th></tr></thead>
                <tbody>{r.rows.map((row, i) => <tr key={i}><td>{row.net}</td><td>{row.range}</td><td>{row.bc}</td></tr>)}</tbody>
              </table>
            </>
          )}
        </CNetResult>
      )}
      {r && r.error && <div className="text-red-600 mt-3">{r.error}</div>}
    </>
  );
}

// ---------------- IPv6 ----------------
function expandV6(ip) {
  if (!ip.includes(':')) return null;
  let [addr, zid] = [ip, ''];
  if (addr.includes('%')) [addr, zid] = addr.split('%');
  let [head, tail] = addr.split('::');
  const h = head ? head.split(':') : [];
  const t = tail !== undefined ? (tail === '' ? [] : tail.split(':')) : null;
  let parts;
  if (t === null) parts = h;
  else {
    const missing = 8 - (h.length + t.length);
    parts = [...h, ...Array(missing).fill('0'), ...t];
  }
  if (parts.length !== 8) return null;
  return parts.map(p => p.padStart(4, '0')).join(':').toLowerCase();
}

function IPv6() {
  const [prefix, setPrefix] = useState(64);
  const [ip, setIp] = useState('2001:db8:85a3::8a2e:370:7334');
  const [r, setR] = useState(null);
  const calc = () => {
    const full = expandV6(ip.trim());
    if (!full) { setR({ error: 'Invalid IPv6 address' }); return; }
    const bits = full.split(':').map(g => parseInt(g, 16).toString(2).padStart(16, '0')).join('');
    const net = bits.slice(0, prefix).padEnd(128, '0');
    const rangeEnd = bits.slice(0, prefix).padEnd(128, '1');
    const chunks = (s) => s.match(/.{1,16}/g).map(g => parseInt(g, 2).toString(16).padStart(4, '0')).join(':');
    const total = BigInt(2) ** BigInt(128 - prefix);
    setR({ ip: `${ip}/${prefix}`, full, total: total.toLocaleString(), net: chunks(net).replace(/(:0000)+$/, '::').replace(/(:0000)+/g, ':'), first: chunks(net), last: chunks(rangeEnd) });
  };
  return (
    <>
      <h3 className="cnet-section-title mt-6">IPv6 Subnet Calculator</h3>
      <div className="cnet-panel">
        <div className="cnet-row-2col">
          <div className="cnet-label">Prefix Length:</div>
          <select className="cnet-select" value={prefix} onChange={e => setPrefix(+e.target.value)}>
            {[64, 48, 56, 32, 128, 96, 80].map(p => <option key={p} value={p}>/{p}</option>)}
          </select>
        </div>
        <div className="cnet-row-2col">
          <div className="cnet-label">IP Address:</div>
          <input className="cnet-input" value={ip} onChange={e => setIp(e.target.value)} />
        </div>
        <CNetActions onCalc={calc} onClear={() => setR(null)} testId="subnet-v6" />
      </div>
      {r && !r.error && (
        <CNetResult>
          <table className="cnet-info-table" data-testid="subnet-v6-table">
            <tbody>
              <tr><td>IP Address:</td><td>{r.ip}</td></tr>
              <tr><td>Full IP Address:</td><td>{r.full}</td></tr>
              <tr><td>Total IP Addresses:</td><td>{r.total}</td></tr>
              <tr><td>Network:</td><td>{r.net}</td></tr>
              <tr><td>IP Range:</td><td className="break-all">{r.first} - {r.last}</td></tr>
            </tbody>
          </table>
        </CNetResult>
      )}
      {r && r.error && <div className="text-red-600 mt-3">{r.error}</div>}
    </>
  );
}

export default function IPSubnetCalc() {
  return (
    <CalcShell look="cnet" showActions={false} title="IP Subnet Calculator" subtitle="This calculator returns a variety of information regarding Internet Protocol version 4 (IPv4) and IPv6 subnets including possible network addresses, usable host ranges, subnet mask, and IP class, among others.">
      <IPv4 />
      <IPv6 />
    </CalcShell>
  );
}
